import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  Upload, 
  File, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  Archive,
  Code
} from 'lucide-react';

const FileUploader = ({ onFileSelect, onScanStart, isScanning = false, maxFileSize = 500 * 1024 * 1024 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  // File type icons mapping
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return <FileImage className="h-6 w-6 text-blue-400" />;
    } else if (['mp4', 'avi', 'mov', 'mkv', 'wmv', 'flv'].includes(extension)) {
      return <FileVideo className="h-6 w-6 text-purple-400" />;
    } else if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(extension)) {
      return <FileAudio className="h-6 w-6 text-green-400" />;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return <Archive className="h-6 w-6 text-yellow-400" />;
    } else if (['js', 'ts', 'py', 'java', 'cpp', 'html', 'css'].includes(extension)) {
      return <Code className="h-6 w-6 text-cyan-400" />;
    } else {
      return <FileText className="h-6 w-6 text-gray-400" />;
    }
  };

  // Validate file size and type
  const validateFile = (file) => {
    const errors = [];
    
    if (file.size > maxFileSize) {
      errors.push(`File "${file.name}" is too large. Maximum size is ${Math.round(maxFileSize / 1024 / 1024)}MB`);
    }
    
    // Add any other validation rules here
    return errors;
  };

  // Handle file selection
  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    const newErrors = [];
    const validFiles = [];

    fileArray.forEach(file => {
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        newErrors.push(...fileErrors);
      } else {
        validFiles.push({
          file,
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          type: file.type || 'unknown',
          status: 'pending'
        });
      }
    });

    setErrors(newErrors);
    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    if (validFiles.length > 0 && onFileSelect) {
      onFileSelect(validFiles);
    }
  }, [maxFileSize, onFileSelect]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // File input handler
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  // Remove file from selection
  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // Start scanning selected files
  const handleScanAll = () => {
    if (selectedFiles.length === 0) return;
    
    // Set all files to scanning status
    setSelectedFiles(prev => prev.map(file => ({ ...file, status: 'scanning' })));
    
    if (onScanStart) {
      onScanStart(selectedFiles);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-gray-400 border-gray-600">Pending</Badge>;
      case 'scanning':
        return <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/50">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Scanning
        </Badge>;
      case 'clean':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/50">
          <CheckCircle className="h-3 w-3 mr-1" />
          Clean
        </Badge>;
      case 'threat':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/50">
          <AlertCircle className="h-3 w-3 mr-1" />
          Threat Detected
        </Badge>;
      case 'error':
        return <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/50">
          <X className="h-3 w-3 mr-1" />
          Error
        </Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
          accept="*/*"
        />
        
        <div className="space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
            dragActive ? 'bg-blue-100' : 'bg-gray-100'
          }`}>
            <Upload className={`h-8 w-8 transition-colors ${
              dragActive ? 'text-blue-600' : 'text-gray-500'
            }`} />
          </div>
          
          <div>
            <p className="text-lg font-medium text-gray-800">
              {dragActive ? 'Drop files here' : 'Drag and drop files to scan'}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              or{' '}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-500 underline"
              >
                browse to upload
              </button>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Maximum file size: {Math.round(maxFileSize / 1024 / 1024)}MB
            </p>
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map((error, index) => (
            <Alert key={index} className="border-red-500/50 bg-red-500/10">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-300">{error}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-800">
              Selected Files ({selectedFiles.length})
            </h3>
            <Button
              onClick={handleScanAll}
              disabled={isScanning || selectedFiles.length === 0}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Scan All Files
                </>
              )}
            </Button>
          </div>

          <div className="space-y-3">
            {selectedFiles.map((fileInfo) => (
              <div
                key={fileInfo.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileIcon(fileInfo.name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {fileInfo.name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatFileSize(fileInfo.size)} • {fileInfo.type}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {getStatusBadge(fileInfo.status)}
                  {fileInfo.status === 'pending' && (
                    <Button
                      onClick={() => removeFile(fileInfo.id)}
                      variant="outline"
                      size="sm"
                      className="p-2 border-gray-300 text-gray-600 hover:text-red-600 hover:border-red-300"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scanning Progress */}
      {isScanning && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Scanning progress</span>
              <span className="text-sm text-blue-600">Processing files...</span>
            </div>
            <Progress value={45} className="bg-gray-200" />
            <p className="text-xs text-gray-600 text-center">
            Using 70+ antivirus engines for comprehensive analysis
          </p>
        </div>
      )}
    </div>
  );
};

export default FileUploader;